import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Store, LogOut, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";

export default function OwnerLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { user, loading: authLoading, login, logout } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            if (u.role === "admin") {
                toast.info("Account amministratore — reindirizzo al backoffice.");
                navigate("/admin");
            } else if (u.role === "store_owner") {
                navigate("/rivenditore");
            } else {
                toast.error("Ruolo non riconosciuto.");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const alreadyLogged = !authLoading && user;

    return (
        <div className="min-h-screen flex flex-col bg-magni-cream">
            <SiteHeader />
            <Toaster position="top-center" richColors />
            <main className="flex-1 flex items-center justify-center px-4 py-16">
                <div className="w-full max-w-md bg-white border border-magni-light p-10">
                    <div className="flex items-center gap-2 mb-2">
                        <Store className="w-5 h-5 text-magni-navy" />
                        <div className="overline">Area rivenditore</div>
                    </div>
                    <h1 className="font-serif-display text-3xl text-magni-navy mb-2">
                        Accesso negozio
                    </h1>
                    <p className="text-sm text-magni-muted mb-8">
                        Visualizza e gestisci le prenotazioni MagniStretch® del tuo punto vendita.
                    </p>

                    {alreadyLogged && user.role === "store_owner" && (
                        <div className="bg-magni-celeste border border-magni-light p-4 mb-6 text-sm">
                            Sei già loggato come <strong>{user.email}</strong>.
                            <div className="mt-3 flex gap-3">
                                <button
                                    onClick={() => navigate("/rivenditore")}
                                    className="magni-btn-primary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    Vai alla dashboard
                                </button>
                                <button
                                    onClick={async () => { await logout(); }}
                                    className="magni-btn-ghost"
                                >
                                    <LogOut className="w-4 h-4 mr-1" /> Esci
                                </button>
                            </div>
                        </div>
                    )}

                    {alreadyLogged && user.role === "admin" && (
                        <div className="bg-yellow-50 border border-yellow-200 p-4 mb-6 text-sm text-magni-navy">
                            <div className="flex items-start gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 text-yellow-600 flex-shrink-0" />
                                <div>
                                    Sei loggato come <strong>{user.email}</strong> (admin).
                                    Per accedere come rivenditore esci prima.
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => navigate("/admin")}
                                    className="magni-btn-secondary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    Vai al backoffice
                                </button>
                                <button
                                    onClick={async () => { await logout(); }}
                                    className="magni-btn-primary"
                                    style={{ padding: "10px 18px", fontSize: "12px" }}
                                >
                                    <LogOut className="w-4 h-4 mr-1" /> Esci e cambia account
                                </button>
                            </div>
                        </div>
                    )}

                    {!alreadyLogged && (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                    Email
                                </label>
                                <input
                                    data-testid="owner-email-input"
                                    type="email"
                                    required
                                    className="magni-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                    Password
                                </label>
                                <input
                                    data-testid="owner-password-input"
                                    type="password"
                                    required
                                    className="magni-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <button
                                data-testid="owner-login-submit"
                                type="submit"
                                disabled={loading}
                                className="magni-btn-primary w-full"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Accesso...
                                    </>
                                ) : (
                                    "Accedi"
                                )}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-6 border-t border-magni-light text-xs text-magni-muted text-center space-y-2">
                        <div>Le credenziali ti sono fornite da Magniflex.</div>
                        <div>
                            Sei un amministratore?{" "}
                            <Link to="/admin/login" className="text-magni-navy hover:underline font-semibold">
                                Vai al backoffice →
                            </Link>
                        </div>
                        <div>
                            <Link to="/" className="hover:text-magni-navy">
                                ← Torna al sito pubblico
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
